import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EditorState } from '@tiptap/pm/state';
import { CitationProps } from './CitationPanel';
import { Alert, Box, Button, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import type { Editor } from '@tiptap/react';
import TextEditor from 'shared-components/components/TextEditor/TextEditor';
import { Logger } from 'shared-components/utils';

const logger = new Logger('EditLetterPanel');
import DOMPurify from 'dompurify';

const AUTOSAVE_INTERVAL = 5000; // 5 seconds

/**
 * Error boundary to prevent TipTap/editor crashes from logging the user out
 * or crashing the entire app. Renders a fallback UI instead.
 */
class LetterEditorErrorBoundary extends React.Component<
  { children: React.ReactNode; onClose?: () => void },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; onClose?: () => void }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to Sentry so we can see it even if page redirects
    logger.error('componentDidCatch', 'Unhandled error in letter editor');
  }

  render() {
    if (this.state.hasError) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 4, gap: 2 }}>
          <Typography variant="h6" color="error">
            Unable to display the document editor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            An error occurred while rendering the editor. Your last saved content is preserved.
          </Typography>
          {this.props.onClose && (
            <Button variant="outlined" onClick={this.props.onClose} data-testid="close-letter-panel-button">
              Close
            </Button>
          )}
        </Stack>
      );
    }
    return this.props.children;
  }
}

const EditLetterPanel = ({
  documentUrl,
  onClose,
  letterContent,
  onLetterChange,
  onLetterSave,
  aiLetterId,
  onSendToFhir,
  emrDocumentId,
  updatedAt,
  isEditorDisabled = false,
}: CitationProps): JSX.Element => {
  const isControlled = letterContent !== undefined;
  const [internalContent, setInternalContent] = useState<string>('');
  // In controlled mode, treat empty content as still-loading so the editor never
  // mounts with TipTap's built-in "Getting started" placeholder template.
  const [loading, setLoading] = useState(!isControlled || letterContent === undefined);
  const [fetchError, setFetchError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const handleRetryFetch = () => setRetryToken((count) => count + 1);
  // Local timestamp updated immediately after each save.
  // Seeded from the updatedAt prop on mount, or when the letter identity (aiLetterId) changes.
  // We intentionally do NOT re-sync from updatedAt on every prop change — the REST save
  // updates the DB but the Apollo query doesn't refetch automatically, so updatedAt would
  // be stale and overwrite the locally-set timestamp from saveAndTimestamp().
  const [lastSavedAt, setLastSavedAt] = useState<string | undefined>(updatedAt);
  const prevAiLetterIdRef = useRef<number | undefined>(aiLetterId);
  useEffect(() => {
    // Sync from prop when:
    // 1. A different letter is opened (aiLetterId changed), OR
    // 2. The prop carries a newer timestamp than what we have locally
    //    (happens after Apollo refetches following a save)
    const isNewLetter = aiLetterId !== prevAiLetterIdRef.current;
    const isPropNewer = updatedAt && (!lastSavedAt || new Date(updatedAt) > new Date(lastSavedAt));
    if (isNewLetter || isPropNewer) {
      prevAiLetterIdRef.current = aiLetterId;
      if (updatedAt) setLastSavedAt(updatedAt);
    }
  }, [aiLetterId, updatedAt]); // lastSavedAt intentionally excluded — comparing only, not retriggering

  const content = isControlled ? letterContent : internalContent;
  const isEmptyContent = !isControlled && !loading && !fetchError && (content ?? '').trim() === '';

  const decodeHtmlEntities = (htmlContent: string | undefined): string => {
    if (!htmlContent) return '';
    if (htmlContent.includes('&lt;') || htmlContent.includes('&gt;')) {
      const textarea = document.createElement('textarea');
      // Sanitize content before setting innerHTML to prevent XSS
      textarea.innerHTML = DOMPurify.sanitize(htmlContent);
      return textarea.value;
    }
    return htmlContent;
  };

  const editorRef = useRef<Editor | null>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);

  // Track if the change came from user typing (internal) vs external update
  const isInternalChangeRef = useRef<boolean>(false);
  // Track the last content we set to the editor to avoid unnecessary updates
  const lastSetContentRef = useRef<string>('');
  // When true, the current setContent call originated from an external update
  // (e.g. AI edit) so handleContentChange should NOT mark it as internal.
  const isApplyingExternalRef = useRef<boolean>(false);
  // Track if content is dirty (changed since last save)
  const isDirtyRef = useRef<boolean>(false);
  const isSavingRef = useRef<boolean>(false);
  const saveCounterRef = useRef<number>(0);

  const handleEditorReady = (editor: Editor | null) => {
    editorRef.current = editor;
    setIsEditorReady(!!editor);
  };

  const handleContentChange = (html: string) => {
    // If this update was triggered by an external setContent (AI edit), don't
    // mark it as internal — otherwise the next external update would be skipped.
    if (isApplyingExternalRef.current) return;
    // Mark this as an internal change so we don't re-sync it back
    isInternalChangeRef.current = true;
    // Mark as dirty for autosave
    isDirtyRef.current = true;
    saveCounterRef.current++;

    if (isControlled && onLetterChange) {
      onLetterChange(html);
    } else {
      setInternalContent(html);
    }
  };

  const saveAndTimestamp = useCallback(async (): Promise<void> => {
    if (!onLetterSave || isSavingRef.current) return;
    const saveToken = saveCounterRef.current;
    isSavingRef.current = true;

    try {
      await onLetterSave();
      setLastSavedAt(new Date().toISOString());
      if (saveCounterRef.current === saveToken) {
        isDirtyRef.current = false;
      }
    } catch (error) {
      isDirtyRef.current = true;
      logger.error('saveAndTimestamp', 'Failed to save letter');
    } finally {
      isSavingRef.current = false;
    }
  }, [onLetterSave]);

  const saveBeforeClose = async (): Promise<void> => {
    if (isDirtyRef.current && onLetterSave) {
      await saveAndTimestamp();
    }
  };

  const handleCloseClick = () => {
    saveBeforeClose().finally(() => onClose?.());
  };

  // Sync content to editor ONLY for external changes
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !isEditorReady) return;

    if (content) {
      const validContent = decodeHtmlEntities(content);

      // If this change came from user typing, skip the sync to avoid
      // replacing the editor doc mid-keystroke and resetting cursor position.
      if (isInternalChangeRef.current) {
        isInternalChangeRef.current = false;
        lastSetContentRef.current = validContent;
        return;
      }

      // External update (AI edit / prop change from outside).
      // Only apply if content actually changed to avoid no-op setContent calls.
      if (validContent === lastSetContentRef.current) return;

      const isInitialLoad = lastSetContentRef.current === '';
      lastSetContentRef.current = validContent;

      const applyContent = (html: string, preserveHistory: boolean) => {
        if (preserveHistory) {
          // For AI edits: replace content as an undoable transaction so the user
          // can Cmd+Z back to their previous state.
          editor.commands.setContent(html, { emitUpdate: true });
        } else {
          // For the initial load: suppress the update event then wipe history so
          // Undo can't go back to the empty "Getting started" template state.
          editor.commands.setContent(html, { emitUpdate: false });
          editor.view.updateState(
            EditorState.create({
              schema: editor.state.schema,
              plugins: editor.state.plugins,
              doc: editor.state.doc,
              selection: editor.state.selection,
            }),
          );
        }
      };

      try {
        isApplyingExternalRef.current = true;
        applyContent(validContent, !isInitialLoad);
      } catch (error) {
        // If the editor can't parse the HTML, strip unsupported tags and retry
        try {
          const sanitized = validContent.replace(/<\/?mark[^>]*>/gi, '');
          applyContent(sanitized, !isInitialLoad);
        } catch {
          logger.error('applyContent', 'Failed to apply content after sanitization retry');
        }
      } finally {
        isApplyingExternalRef.current = false;
      }
    } else if (lastSetContentRef.current !== '') {
      lastSetContentRef.current = '';
      editor.commands.clearContent();
    }
  }, [content, isEditorReady]);

  // Autosave effect
  useEffect(() => {
    if (!onLetterSave) return;

    const intervalId = setInterval(() => {
      if (isDirtyRef.current) {
        saveAndTimestamp();
      }
    }, AUTOSAVE_INTERVAL);

    return () => clearInterval(intervalId);
  }, [saveAndTimestamp]);

  // Save on unmount if dirty (e.g. when closing panel or navigating away)
  useEffect(() => {
    return () => {
      if (isDirtyRef.current && onLetterSave) {
        onLetterSave().catch((error) => {
          logger.error('unmountSave', 'Failed to save letter on unmount');
        });
      }
    };
  }, [onLetterSave]);

  // In controlled mode, clear loading once real content arrives
  useEffect(() => {
    if (isControlled && letterContent !== undefined) {
      setLoading(false);
    }
  }, [isControlled, letterContent]);

  // Fetch content for uncontrolled mode
  useEffect(() => {
    if (isControlled) {
      return;
    }

    if (!documentUrl) return;

    // Reset stale content so the editor clears while the new URL loads
    setInternalContent('');
    setLoading(true);
    setFetchError(false);

    const fetchContent = async () => {
      try {
        const response = await fetch(documentUrl, {
          method: 'GET',
          mode: 'cors',
          headers: {
            Accept: 'text/html,text/*,*/*',
            Origin: window.location.origin,
          },
        });
        if (!response.ok) throw new Error('Failed to fetch content');
        const text = await response.text();
        setInternalContent(text);
      } catch (err) {
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [documentUrl, isControlled, retryToken]);

  return (
    <LetterEditorErrorBoundary onClose={onClose}>
      <style>
        {`
          /* Ensure TextEditor Box wrapper takes full height */
          .tiptap-editor-content > div[class*="MuiBox"] {
            height: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            flex: 1 !important;
            min-height: 0 !important;
          }
          
          /* Ensure SimpleEditor wrapper takes full height */
          .tiptap-editor-content .simple-editor-wrapper {
            height: 100% !important;
            max-height: 100% !important;
            overflow: auto !important;
            display: flex !important;
            flex-direction: column !important;
            flex: 1 !important;
            min-height: 0 !important;
          }
          
          /* Keep toolbar fixed at top */
          .tiptap-editor-content .simple-editor-wrapper [role="toolbar"] {
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
            background-color: white !important;
            flex-shrink: 0 !important;
          }
          
          /* Ensure SimpleEditor content container expands to fit content */
          .tiptap-editor-content .simple-editor-content {
            height: auto !important;
            min-height: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            flex: 1 !important;
            overflow: visible !important;
          }
          
          /* Reduce left and right padding on the editor canvas */
          .tiptap-editor-content .ProseMirror.simple-editor {
            padding-left: 16px !important;
            padding-right: 16px !important;
            padding-top: 24px !important;
            padding-bottom: 250px !important;
            flex: 1 !important;
            min-height: fit-content !important;
            overflow: visible !important;
          }
          
          /* Ensure the editor container allows scrolling */
          .tiptap-editor-container {
            height: 100% !important;
            overflow: visible !important;
            display: flex !important;
            flex-direction: column !important;
            flex: 1 !important;
            min-height: 0 !important;
          }
          
          .tiptap-editor-content {
            height: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            flex: 1 !important;
            min-height: 0 !important;
          }
          
          /* Ensure list items have the same font-size as paragraphs (1rem) */
          .tiptap-editor-content .ProseMirror li {
            font-size: 1rem;
            line-height: 1.6;
          }
          
          /* Ensure inline elements (strong, em, etc.) in list items inherit the same font-size */
          .tiptap-editor-content .ProseMirror li strong,
          .tiptap-editor-content .ProseMirror li em,
          .tiptap-editor-content .ProseMirror li u,
          .tiptap-editor-content .ProseMirror li code {
            font-size: 1rem;
          }
        `}
      </style>
      <Stack
        className="main-stack"
        sx={{
          height: '100%',
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Stack
          direction="row"
          gap={1}
          sx={{
            padding: '0 24px',
            height: '64px',
            alignItems: 'center',
            flexShrink: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}>
          <IconButton
            sx={{ marginLeft: 0 }}
            size="small"
            onClick={handleCloseClick}
            aria-label="Close letter canvas"
            data-testid="close-letter-canvas-button">
            <CloseOutlined />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            RO-Consult
          </Typography>
          {(!emrDocumentId || emrDocumentId === '') && (
            <Typography
              variant="caption"
              sx={{
                color: 'warning.dark',
                borderColor: 'warning.dark',
                border: '1px solid',
                px: 1.5,
                py: 0.25,
                borderRadius: '12px',
                fontWeight: 500,
                fontSize: '0.6875rem',
                letterSpacing: '0.5px',
              }}>
              Draft
            </Typography>
          )}
          {/* TODO: Add send to EMR button */}
          {/* {onSendToFhir && (
            <Button
              variant="outlined"
              onClick={onSendToFhir}
              sx={{
                marginLeft: 'auto',
                marginRight: 1,
                borderRadius: '4px',
                border: '1px solid',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                },
              }}
              aria-label="Submit Document">
              Approve
            </Button>
          )} */}
        </Stack>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            minHeight: 0,
          }}>
          {loading ? (
            <Box sx={{ p: 2 }}>
              <Skeleton variant="rectangular" height={240} />
            </Box>
          ) : fetchError ? (
            <Alert
              severity="error"
              sx={{ m: 2 }}
              action={
                <Button size="small" onClick={handleRetryFetch} data-testid="retry-fetch-button">
                  Retry
                </Button>
              }>
              Error loading content
            </Alert>
          ) : isEmptyContent ? (
            <Typography variant="body2" sx={{ p: 2 }} color="text.secondary">
              No content available.
            </Typography>
          ) : (
            <Box
              className="tiptap-editor-container"
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}>
              <div className="tiptap-editor-content">
                <TextEditor
                  onEditorReady={handleEditorReady}
                  onContentChange={handleContentChange}
                  editable={!isEditorDisabled}
                  initialContent={decodeHtmlEntities(content)}
                />
              </div>
            </Box>
          )}
        </Box>
      </Stack>
    </LetterEditorErrorBoundary>
  );
};

export default EditLetterPanel;
